import { Link } from 'react-router-dom'
import { Play } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { AddToCartButton } from '@/components/cart/add-to-cart-button'
import { ShareButton } from '@/components/share-button'
import { cn } from '@/lib/utils'
import { getProductThumbnail, getProductMedia } from '@/lib/product-media'
import type { Product, ProductOption, ProductVariant } from '@/types/database'

export type ProductCardVariant =
  | 'default'
  | 'featured'
  | 'compact'
  | 'archive-default'
  | 'archive-compact'
  | 'featured-preview'

interface ProductCardProps {
  product: Product
  discountPercent?: number
  className?: string
  variant?: ProductCardVariant
  showAddToCart?: boolean
}

function resolveVariant(variant: ProductCardVariant): Exclude<ProductCardVariant, 'default' | 'featured' | 'compact'> {
  if (variant === 'default') return 'archive-default'
  if (variant === 'compact') return 'archive-compact'
  if (variant === 'featured') return 'featured-preview'
  return variant
}

function getDateLabel(product: Product) {
  if (!product.featured_end_date) return null
  const endDate = new Date(product.featured_end_date)
  if (Number.isNaN(endDate.getTime())) return null
  return `Week of ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
}

export function ProductCard({
  product,
  discountPercent,
  className,
  variant = 'default',
  showAddToCart = true,
}: ProductCardProps) {
  const resolvedVariant = resolveVariant(variant)
  const discount = discountPercent ?? (
    product.status === 'featured'
      ? product.featured_discount_percent
      : product.archive_discount_percent
  )

  const discountedPrice = product.original_price * (1 - discount / 100)
  const savingsAmount = product.original_price - discountedPrice
  const thumbnail = getProductThumbnail(product)
  const media = getProductMedia(product)
  const hasVideo = media.some(item => item.type === 'video')
  const dateLabel = getDateLabel(product)

  // Check for product options / variants
  const options = (product.options as unknown as ProductOption[]) || []
  const variants = (product.variants as unknown as ProductVariant[]) || []
  const hasOptions = options.some(o => o.values.length > 0)

  // Calculate price range if variants have different adjustments
  const priceAdjustments = variants.map(v => v.price_adjustment).filter(a => a !== 0)
  const hasVariedPricing = priceAdjustments.length > 0 && new Set(priceAdjustments).size > 1
  const minEffective = hasVariedPricing
    ? (product.original_price + Math.min(...priceAdjustments.map(Number))) * (1 - discount / 100)
    : discountedPrice

  return (
    <Card
      className={cn(
        'group flex h-full flex-col overflow-hidden rounded-2xl border border-zinc-200/90 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl',
        resolvedVariant === 'featured-preview' && 'border-red-200 shadow-red',
        className
      )}
    >
      <Link
        to={`/review/${product.slug}`}
        className={cn(
          'relative block overflow-hidden bg-zinc-100',
          resolvedVariant === 'archive-compact' ? 'aspect-video' : 'aspect-[4/3]'
        )}
      >
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={product.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-zinc-100 text-center">
            <span className="px-4 text-sm font-medium text-zinc-500">Image coming soon</span>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent p-3">
          {hasVideo && (
            <div className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-xs font-semibold text-zinc-900">
              <Play className="size-3.5" fill="currentColor" />
              Watch review
            </div>
          )}
        </div>

        {discount > 0 && (
          <div className="absolute left-3 top-3 rounded-full bg-red-600 px-2.5 py-1 text-xs font-bold text-white shadow">
            {discount}% OFF
          </div>
        )}

        {resolvedVariant === 'featured-preview' && (
          <div className="absolute right-3 top-3 rounded-full border border-white/30 bg-zinc-950/80 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur">
            This Week
          </div>
        )}

        {/* Share icon on hover */}
        <div className="absolute right-3 bottom-3 z-10 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <ShareButton
            url={`${window.location.origin}/review/${product.slug}`}
            title={product.title}
            variant="icon"
            className="bg-white/90 shadow-sm hover:bg-white"
          />
        </div>
      </Link>

      <div className={cn('flex flex-1 flex-col', resolvedVariant === 'archive-compact' ? 'p-4' : 'p-5')}>
        <div className="mb-3 space-y-1.5">
          {dateLabel && (
            <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{dateLabel}</p>
          )}
          <Link
            to={`/review/${product.slug}`}
            className={cn(
              'line-clamp-2 font-semibold text-zinc-900 transition-colors hover:text-red-600',
              resolvedVariant === 'archive-compact' ? 'text-base' : 'text-xl'
            )}
          >
            {product.title}
          </Link>
          {resolvedVariant !== 'archive-compact' && product.short_description && (
            <p className="line-clamp-2 text-sm text-zinc-600">{product.short_description}</p>
          )}
        </div>

        <div className="mb-4 rounded-xl border border-zinc-200 bg-zinc-50/80 p-3">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">Now</p>
              <p className="text-2xl font-bold text-zinc-900">
                {hasVariedPricing ? `From $${minEffective.toFixed(2)}` : `$${discountedPrice.toFixed(2)}`}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">Was</p>
              <p className="text-sm text-zinc-400 line-through">${product.original_price.toFixed(2)}</p>
            </div>
          </div>
          {discount > 0 && (
            <p className="mt-2 text-sm font-medium text-emerald-600">Save ${savingsAmount.toFixed(2)}</p>
          )}
        </div>

        <div className="mt-auto flex items-center gap-2">
          {showAddToCart ? (
            hasOptions ? (
              <>
                <Button asChild size="sm" className="h-10 flex-1 bg-red-600 font-semibold hover:bg-red-700">
                  <Link to={`/review/${product.slug}`}>
                    Select Options
                    {variants.length > 0 && (
                      <span className="ml-1.5 rounded bg-red-500/30 px-1.5 py-0.5 text-[10px]">
                        {variants.length}
                      </span>
                    )}
                  </Link>
                </Button>
              </>
            ) : (
              <>
                <AddToCartButton productId={product.id} size="sm" className="h-10 flex-1 font-semibold" />
                <Button asChild variant="outline" size="sm" className="h-10 px-4 font-medium">
                  <Link to={`/review/${product.slug}`}>Details</Link>
                </Button>
              </>
            )
          ) : (
            <Button asChild size="sm" className="h-10 w-full bg-red-600 font-semibold hover:bg-red-700">
              <Link to={`/review/${product.slug}`}>View Deal</Link>
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}
